import { CSSProperties } from 'react';
import { useRecoilValue } from 'recoil';
import { iconStylesState, IconTheme } from 'styles/stylesProvider';

const defaultSize = '15px';

type SvgIconDatum = {
  box: string;
  fill: string;
  path: string;
};

type SvgIconData = {
  info: SvgIconDatum;
  warning: SvgIconDatum;
  error: SvgIconDatum;
};

const getSvgIcons = (styles: IconTheme): SvgIconData => ({
  info: {
    box: '0 0 74 74',
    fill: styles.info,
    path: 'M37,0C16.57,0,0,16.57,0,37c0,20.43,16.57,37,37,37c20.43,0,37-16.57,37-37C74,16.57,57.43,0,37,0z M41.71,57.99h-9v-29h9V57.99z M36.97,25.76c-2.8,0-5.12-2.32-5.12-5.19c0-2.87,2.32-5.12,5.12-5.12c2.87,0,5.19,2.25,5.19,5.12 C42.15,23.44,39.83,25.76,36.97,25.76z',
  },
  warning: {
    box: '0 0 82 74',
    fill: styles.warning,
    path: 'M80.01,59.97C69.26,40.98,59.36,23.63,49.74,6.91C48.04,3.94,45.27,0,40.94,0c-4.33,0-7.07,3.92-8.75,6.87 C21.67,25.29,11.14,43.81,2.21,59.49c-0.89,1.57-3.62,6.36-1.3,10.39c2.31,4.01,7.73,4.05,9.51,4.07c3.63,0.03,7.51,0.04,12.22,0.04 c3.21,0,6.42-0.01,9.63-0.01c2.9-0.01,5.79-0.01,8.69-0.01c3.05,0,6.1,0.01,9.15,0.02c3.06,0.01,6.12,0.02,9.18,0.02 c4.93,0,8.97-0.02,12.72-0.07c4.5-0.06,7.56-1.42,9.07-4.07C82.6,67.22,82.24,63.89,80.01,59.97z M40.96,63.99 c-2.8,0-5.13-2.33-5.13-5.21c0-2.88,2.33-5.14,5.13-5.14c2.87,0,5.2,2.26,5.2,5.14C46.16,61.66,43.84,63.99,40.96,63.99z M47.41,28.2c-1.09,7.04-2.9,13.98-4.55,20.92c-0.17,0.7-1.31,1.73-1.86,1.74c-0.54,0-1.69-1.04-1.86-1.74 c-1.65-6.94-3.46-13.88-4.55-20.92c-0.62-3.99,2.56-6.88,6.41-6.95C44.85,21.32,48.03,24.21,47.41,28.2z',
  },
  error: {
    box: '0 0 82 74',
    fill: styles.error,
    path: 'M79.99,60.05C69.24,41.06,59.33,23.7,49.72,6.98c-1.71-2.97-4.47-6.91-8.81-6.91c-4.33,0-7.07,3.92-8.75,6.87 C21.65,25.37,11.11,43.89,2.19,59.57c-0.89,1.57-3.62,6.36-1.3,10.39c2.31,4.01,7.73,4.05,9.51,4.07c3.63,0.03,7.51,0.04,12.22,0.04 c3.21,0,6.42-0.01,9.63-0.01c2.9-0.01,5.79-0.01,8.69-0.01c3.05,0,6.1,0.01,9.15,0.02c3.06,0.01,6.12,0.02,9.18,0.02 c4.93,0,8.97-0.02,12.72-0.07c4.5-0.06,7.56-1.42,9.07-4.07C82.57,67.3,82.21,63.97,79.99,60.05z M43.59,56.9 c1.5-0.22,2.6-0.38,4.7-0.68c-3.62,3.37-6.48,6.02-9.73,9.05c-0.82-3.39-1.64-6.8-2.64-10.9c1.46,0.44,2.36,0.7,3.56,1.06 c1.14-4.18,2.26-8.3,3.63-13.33c-4.67,1.31-8.42,2.35-13.14,3.67c2.59-8.71,4.61-16.54,7.42-24.08c0.53-1.42,4.22-1.66,7.88-2.95 c-2.98,7.96-5.35,14.27-8.11,21.63c5.18-1.4,9.61-2.61,14.88-4.03C49.06,43.59,46.44,49.98,43.59,56.9z',
  },
});

/**
 * Configurable SVG icon class. The icon that's returned is determined by type and
 * must be one of the known types.
 *
 * @prop type      which icon to return
 * @prop height    icon height
 * @prop width     icon width
 */

type SvgIconProps = {
  type: keyof SvgIconData;
  height?: string;
  width?: string;
};

function SvgIcon({ type, height = defaultSize, width = defaultSize }: SvgIconProps): JSX.Element {
  const iconStyles = useRecoilValue(iconStylesState);
  const icons = getSvgIcons(iconStyles);
  const iconData = icons[type] || icons.info;
  const iconStyle: CSSProperties = {
    height,
    width,
    fill: iconData.fill,
  };

  return (
    <svg viewBox={iconData.box} style={iconStyle}>
      <path d={iconData.path} />
    </svg>
  );
}

export default SvgIcon;
